import { apiClient } from './client'

// Leave types are org-configurable, so this is an open string.
export type LeaveType = string

export interface LeaveCondition {
  genders?: string[]
  employment_types?: string[]
  marital_statuses?: string[]
  departments?: string[]
  designations?: string[]
  roles?: string[]
  min_tenure_months?: number
}

export interface QuotaRule {
  when: LeaveCondition
  quota: number
}

export interface LeaveTypeConfig {
  key: string
  label: string
  paid: boolean
  requires_approval: boolean
  default_quota: number
  color: string
  eligibility?: LeaveCondition
  quota_rules?: QuotaRule[]
  min_days?: number | null
  max_days?: number | null
  min_notice_days?: number | null
  // "month" = quota per calendar month (e.g. 2 WFH a month); default "year".
  quota_period?: 'year' | 'month'
  effective_quota?: number   // only present from getEligibleTypes()
}

export async function getLeaveTypes(): Promise<LeaveTypeConfig[]> {
  const { data } = await apiClient.get<LeaveTypeConfig[]>('/organization/leave-types')
  return data
}

export async function setLeaveTypes(types: LeaveTypeConfig[]): Promise<LeaveTypeConfig[]> {
  const { data } = await apiClient.put<LeaveTypeConfig[]>('/organization/leave-types', types)
  return data
}

// The current user's eligible types (+ their effective quota) — for the request form.
export async function getEligibleTypes(): Promise<LeaveTypeConfig[]> {
  const { data } = await apiClient.get<LeaveTypeConfig[]>('/leaves/eligible-types')
  return data
}

export interface LeaveException {
  date: string
  end_date: string
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  approver: string | null
}

export interface LeaveBalance {
  total: number
  used: number
  pending: number
  remaining: number
  // Only for monthly-quota types (e.g. WFH at 2 a month):
  period?: 'month'
  month?: string            // YYYY-MM
  over?: number             // approved days beyond the monthly quota
  exceptions?: LeaveException[]
  fy_used?: number          // monthly types: days used across the whole fiscal year
}

export type LeaveBalances = Record<string, LeaveBalance>

export interface LeaveStep {
  step_number: number
  approver_id: string
  approver_name: string | null
  action: string | null
  action_at: string | null
  forwarded_to_id: string | null
  forwarded_to_name: string | null
  comment: string | null
}

export interface LeaveRequest {
  id: string
  user_id: string
  user_name: string | null
  type: LeaveType
  start_date: string
  end_date: string
  half_day: boolean
  half_day_slot: 'morning' | 'afternoon' | null
  reason: string | null
  working_days: number
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  current_approver_id: string | null
  current_approver_name: string | null
  final_approver_id: string | null
  final_approver_name: string | null
  finalized_at: string | null
  reverses_request_id: string | null
  over_limit?: boolean      // exceeds a monthly allowance (exception request)
  is_backdated?: boolean    // applied for a day that had already passed
  is_direct?: boolean       // recorded instantly (Slack /leaves, /wfh, inform leave, WFH check-in) — no request behind it
  created_at: string
  steps: LeaveStep[]
}

export interface LeaveRequestCreate {
  type: LeaveType
  start_date: string
  end_date: string
  half_day: boolean
  half_day_slot?: 'morning' | 'afternoon'
  reason?: string
  approver_id: string
  reverses_request_id?: string
  // Submit even though it exceeds a monthly allowance (sent as an exception).
  acknowledge_over_limit?: boolean
}

export interface FiscalYear {
  key: number      // calendar year the window starts in (the `year` param of /leaves/balance)
  start: string    // YYYY-MM-DD
  end: string      // YYYY-MM-DD
  label: string    // e.g. "FY 2026–27"
}

export interface LeaveSettings {
  backdate_days: number
  today: string   // YYYY-MM-DD in the user's timezone
  current_fiscal_year?: FiscalYear
  fiscal_years?: FiscalYear[]   // newest first, years with leave history + the current one
}

export async function getLeaveSettings(): Promise<LeaveSettings> {
  const { data } = await apiClient.get<LeaveSettings>('/leaves/settings')
  return data
}

export async function getMyBalance(year?: number): Promise<LeaveBalances> {
  const { data } = await apiClient.get<LeaveBalances>('/leaves/balance', { params: year ? { year } : {} })
  return data
}

export interface LeaveBalanceReport {
  fiscal_year: { key: number; start: string; end: string }
  month: string   // YYYY-MM used for monthly-quota types
  leave_types: { key: string; label: string; color?: string | null; quota_period: 'year' | 'month' }[]
  rows: {
    user_id: string
    name: string
    email: string
    employee_id?: string | null
    department?: string | null
    designation?: string | null
    balances: LeaveBalances
  }[]
}

type BalanceFilters = { year?: number; search?: string; department_id?: string; month?: string }

const cleanParams = (f: BalanceFilters) =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined && v !== ''))

// Leave managers: every active employee's quota / used / pending / remaining.
export async function getAllBalances(filters: BalanceFilters = {}): Promise<LeaveBalanceReport> {
  const { data } = await apiClient.get<LeaveBalanceReport>('/leaves/balances', { params: cleanParams(filters) })
  return data
}

export async function exportAllBalances(filters: BalanceFilters, format: 'csv' | 'xlsx'): Promise<void> {
  const res = await apiClient.get('/leaves/balances/export', {
    params: { ...cleanParams(filters), format },
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `leave-balances.${format}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// Staff: another user's leave balance.
export async function getUserBalance(userId: string, year?: number): Promise<LeaveBalances> {
  const { data } = await apiClient.get<LeaveBalances>(`/leaves/balance/${userId}`, { params: year ? { year } : {} })
  return data
}

export async function createLeaveRequest(payload: LeaveRequestCreate): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>('/leaves/requests', payload)
  return data
}

export async function listMyRequests(statusFilter?: string): Promise<LeaveRequest[]> {
  const { data } = await apiClient.get<LeaveRequest[]>('/leaves/requests/me', {
    params: statusFilter ? { status: statusFilter } : {},
  })
  return data
}

export interface LeaveApprover {
  id: string
  name: string
  email: string
  role: string
  avatar_url?: string | null
  is_manager: boolean
}

// People who can approve a leave request: admins, leave managers, and the
// employee's own manager. `forUserId` evaluates the rule for that employee
// (used when forwarding someone else's request).
export async function listApprovers(search?: string, forUserId?: string): Promise<LeaveApprover[]> {
  const { data } = await apiClient.get<LeaveApprover[]>('/leaves/approvers', {
    params: { ...(search ? { search } : {}), ...(forUserId ? { for_user_id: forUserId } : {}) },
  })
  return data
}

export async function listPendingForMe(): Promise<LeaveRequest[]> {
  const { data } = await apiClient.get<LeaveRequest[]>('/leaves/requests/pending')
  return data
}

export async function listAllRequests(statusFilter?: string): Promise<LeaveRequest[]> {
  const { data } = await apiClient.get<LeaveRequest[]>('/leaves/requests/all', {
    params: statusFilter ? { status: statusFilter } : {},
  })
  return data
}

export async function approveRequest(id: string, comment?: string): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(`/leaves/requests/${id}/approve`, { comment })
  return data
}

export async function rejectRequest(id: string, comment?: string): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(`/leaves/requests/${id}/reject`, { comment })
  return data
}

export async function forwardRequest(id: string, forward_to_id: string, comment?: string): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(`/leaves/requests/${id}/forward`, {
    forward_to_id,
    comment,
  })
  return data
}

export async function cancelRequest(id: string): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(`/leaves/requests/${id}/cancel`)
  return data
}

// Admin
export type DefaultQuotas = Record<string, number>

export async function getDefaultQuotas(): Promise<DefaultQuotas> {
  const { data } = await apiClient.get<DefaultQuotas>('/leaves/quotas/defaults')
  return data
}

export async function setDefaultQuotas(quotas: DefaultQuotas): Promise<DefaultQuotas> {
  const { data } = await apiClient.put<DefaultQuotas>('/leaves/quotas/defaults', { quotas })
  return data
}

export async function getUserQuotas(userId: string, year?: number) {
  const { data } = await apiClient.get(`/leaves/quotas/user/${userId}`, {
    params: year ? { year } : {},
  })
  return data as { user_id: string; year: number; quotas: LeaveBalances; overrides: Record<string, number> }
}

export async function setUserQuota(payload: { user_id: string; year: number; type: string; total_days: number }) {
  await apiClient.put('/leaves/quotas/user', payload)
}
